import { FC, useState } from "react";
import { Car } from "lucide-react";
import { 
  AcuraIcon,
  AudiIcon,
  BMWIcon,
  BentleyIcon,
  CadillacIcon,
  ChevroletIcon,
  ChryslerIcon,
  DodgeIcon,
  FerrariIcon,
  FiatIcon,
  FordIcon,
  GMCIcon,
  GenesisIcon,
  HondaIcon,
  HyundaiIcon,
  InfinitiIcon,
  JaguarIcon,
  JeepIcon,
  KiaIcon,
  LamborghiniIcon,
  LandroverIcon,
  LexusIcon,
  LincolnIcon,
  LotusIcon,
  MBIcon,
  MaseratiIcon,
  MazdaIcon,
  MclarenIcon,
  MiniIcon,
  MitsubishiIcon,
  NissanIcon,
  PorscheIcon,
  RAMIcon,
  RollsRoyceIcon,
  SubaruIcon,
  TeslaIcon,
  ToyotaIcon,
  VolkswagenIcon,
  VolvoIcon,
  AlfaRomeoIcon,
  AstonMartinIcon,
  BYDIcon,
  HummerIcon,
  LucidIcon,
  PolestarIcon,
  VinfastIcon
} from "@cardog-icons/react";

interface CarLogoProps {
  /** The brand name of the car */
  brand: string;
  /** Size of the logo (width and height) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Fallback logo URL when no icon is found */
  fallback?: string;
}

// Function to get the appropriate car icon based on brand name
const getCarIcon = (brandName: string) => {
  if (!brandName) return null;
  
  const normalizedName = brandName.toLowerCase().replace(/\s+/g, '').replace(/[-_]/g, '').replace(/\./g, '');
  
  const iconMap: { [key: string]: any } = {
    // Available cardog icons mapped to brand names
    'acura': AcuraIcon,
    'alfaromeo': AlfaRomeoIcon,
    'alfa': AlfaRomeoIcon,
    'astonmartin': AstonMartinIcon,
    'aston': AstonMartinIcon,
    'audi': AudiIcon,
    'bentley': BentleyIcon,
    'bmw': BMWIcon,
    'byd': BYDIcon,
    'cadillac': CadillacIcon,
    'chevrolet': ChevroletIcon,
    'chevy': ChevroletIcon,
    'chrysler': ChryslerIcon,
    'dodge': DodgeIcon,
    'ferrari': FerrariIcon,
    'fiat': FiatIcon,
    'ford': FordIcon,
    'gmc': GMCIcon,
    'genesis': GenesisIcon,
    'honda': HondaIcon,
    'hummer': HummerIcon,
    'hyundai': HyundaiIcon,
    'infiniti': InfinitiIcon,
    'jaguar': JaguarIcon,
    'jeep': JeepIcon,
    'kia': KiaIcon,
    'lamborghini': LamborghiniIcon,
    'lambo': LamborghiniIcon,
    'landrover': LandroverIcon,
    'landcruiser': LandroverIcon,
    'rover': LandroverIcon,
    'lexus': LexusIcon,
    'lincoln': LincolnIcon,
    'lotus': LotusIcon,
    'lucid': LucidIcon,
    'maserati': MaseratiIcon,
    'mazda': MazdaIcon,
    'mclaren': MclarenIcon,
    'mercedes': MBIcon,
    'mercedesbenz': MBIcon,
    'mb': MBIcon,
    'benz': MBIcon,
    'mini': MiniIcon,
    'mitsubishi': MitsubishiIcon,
    'nissan': NissanIcon,
    'polestar': PolestarIcon,
    'porsche': PorscheIcon,
    'ram': RAMIcon,
    'rollsroyce': RollsRoyceIcon,
    'rolls': RollsRoyceIcon,
    'subaru': SubaruIcon,
    'tesla': TeslaIcon,
    'toyota': ToyotaIcon,
    'vinfast': VinfastIcon,
    'volkswagen': VolkswagenIcon,
    'vw': VolkswagenIcon,
    'volvo': VolvoIcon,
  };
  
  return iconMap[normalizedName] || null;
};

/**
 * CarLogo component that renders the appropriate car brand icon
 * @param brand - The brand name of the car
 * @param size - Size of the logo in pixels (default: 40)
 * @param className - Additional CSS classes
 * @param fallback - Fallback logo URL when no icon is found (default: car icon)
 */
const CarLogo: FC<CarLogoProps> = ({ 
  brand, 
  size = 40, 
  className = "", 
  fallback 
}) => {
  const [imageError, setImageError] = useState(false);
  const IconComponent = getCarIcon(brand);
  
  if (IconComponent) {
    return (
      <IconComponent 
        width={size}
        height={size}
        className={`object-contain ${className}`}
        style={{ maxWidth: `${size}px`, maxHeight: `${size}px` }}
      />
    );
  }
  
  // If fallback URL is provided and no error occurred, render image
  if (fallback && !imageError) {
    return (
      <img
        src={fallback}
        alt={`${brand} logo`}
        width={size}
        height={size}
        className={`object-contain ${className}`}
        style={{ maxWidth: `${size}px`, maxHeight: `${size}px` }}
        onError={() => setImageError(true)}
      />
    );
  }
  
  // Default fallback if no URL provided or image failed to load
  return (
    <div 
      className={`bg-gray-200 border border-gray-300 rounded flex items-center justify-center ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <Car size={size * 0.8} className="text-gray-500" />
    </div>
  );
};

export default CarLogo;